import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  CardGiftcardOutlined,
  MailOutline,
  SwapHorizOutlined,
  TokenOutlined,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Link as RouterLink } from "react-router-dom";
import { GlobalContext } from "../../context/GlobalContext";
import { UserContext } from "../../context/UserContext";

/**
 * Transfer credits to another Beyund AI user by email (logged-in).
 */
export default function TransferCreditsPanel({ lang }) {
  const theme = useTheme();
  const { hitAxios, showSnack } = React.useContext(GlobalContext);
  const { userData, getUserData } = React.useContext(UserContext);

  const [toEmail, setToEmail] = React.useState("");
  const [amount, setAmount] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [result, setResult] = React.useState(null);

  const balance = Number(userData?.credits ?? 0);
  const canSend = balance >= 1;

  const setQuickAmount = (n) => {
    const capped = Math.min(n, balance);
    if (capped >= 1) setAmount(String(capped));
  };

  const handleTransfer = async () => {
    setResult(null);
    const n = Math.floor(Number(amount));
    if (!toEmail.trim()) {
      showSnack(lang?.transferNeedEmail || "Enter the recipient email", false);
      return;
    }
    if (!Number.isFinite(n) || n < 1) {
      showSnack(lang?.transferNeedAmount || "Enter at least 1 credit", false);
      return;
    }
    if (n > balance) {
      showSnack(
        lang?.transferInsufficient ||
          `You only have ${balance.toLocaleString()} credits`,
        false,
      );
      return;
    }

    setSending(true);
    try {
      const res = await hitAxios({
        path: "/api/user/transfer_credits",
        post: true,
        admin: false,
        obj: { to_email: toEmail.trim(), amount: n },
        showLoading: false,
        showSnackbar: false,
      });
      if (res?.data?.success) {
        setResult({ success: true, msg: res.data.msg });
        setAmount("");
        getUserData?.();
        showSnack(res.data.msg, true);
      } else {
        setResult({
          success: false,
          msg: res?.data?.msg || "Transfer failed",
        });
        showSnack(res?.data?.msg || "Transfer failed", false);
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Card
      elevation={0}
      sx={{
        mb: 3.5,
        borderRadius: 3,
        overflow: "hidden",
        border: `1px solid ${theme.palette.divider}`,
        background: `linear-gradient(145deg, ${alpha(
          theme.palette.background.paper,
          1,
        )} 0%, ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.08 : 0.03)} 100%)`,
      }}
    >
      <Box
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: "primary.main",
            }}
          >
            <SwapHorizOutlined />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={800} letterSpacing="-0.01em">
              {lang?.transferTitle || "Transfer credits"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.15 }}>
              {lang?.transferSubtitle ||
                "Send credits to another account by email — instant, no payment."}
            </Typography>
          </Box>
        </Stack>
        <Chip
          icon={<TokenOutlined sx={{ fontSize: "16px !important" }} />}
          label={`${balance.toLocaleString()} available`}
          size="small"
          sx={{
            fontWeight: 700,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          }}
        />
      </Box>

      <CardContent sx={{ px: { xs: 2.5, sm: 3 }, py: 2.5, "&:last-child": { pb: 2.5 } }}>
        {result && (
          <Alert
            severity={result.success ? "success" : "error"}
            sx={{ mb: 2, borderRadius: 2 }}
            onClose={() => setResult(null)}
          >
            {result.msg}
          </Alert>
        )}

        {!canSend && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            {lang?.transferNoBalance ||
              "You need credits in your balance before you can transfer."}
          </Alert>
        )}

        <Stack spacing={2}>
          <TextField
            fullWidth
            type="email"
            label={lang?.transferToEmail || "Recipient email"}
            placeholder="friend@example.com"
            value={toEmail}
            disabled={!canSend}
            onChange={(e) => setToEmail(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MailOutline fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ sm: "flex-start" }}
          >
            <TextField
              label={lang?.transferAmount || "Credits to send"}
              type="number"
              value={amount}
              disabled={!canSend}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{ min: 1, max: balance || 1 }}
              sx={{ width: { xs: "100%", sm: 200 } }}
              helperText={
                canSend
                  ? lang?.transferAmountHint || "Whole credits only"
                  : " "
              }
            />
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              sx={{ pt: { sm: 0.5 } }}
            >
              {[10, 25, 50, 100].map((n) => (
                <Chip
                  key={n}
                  label={n}
                  size="small"
                  clickable={canSend && balance >= n}
                  disabled={!canSend || balance < n}
                  onClick={() => setQuickAmount(n)}
                  variant={String(amount) === String(n) ? "filled" : "outlined"}
                  color={String(amount) === String(n) ? "primary" : "default"}
                  sx={{ fontWeight: 700 }}
                />
              ))}
              {canSend && (
                <Chip
                  label="Max"
                  size="small"
                  clickable
                  onClick={() => setQuickAmount(balance)}
                  variant={String(amount) === String(balance) ? "filled" : "outlined"}
                  color={String(amount) === String(balance) ? "primary" : "default"}
                  sx={{ fontWeight: 700 }}
                />
              )}
            </Stack>
            <LoadingButton
              variant="contained"
              size="large"
              loading={sending}
              disabled={!canSend}
              onClick={handleTransfer}
              startIcon={<SwapHorizOutlined />}
              sx={{
                textTransform: "none",
                fontWeight: 800,
                px: 3,
                ml: { sm: "auto" },
                borderRadius: 2,
                minHeight: 48,
              }}
            >
              {lang?.transferCta || "Send credits"}
            </LoadingButton>
          </Stack>
        </Stack>

        <Divider sx={{ my: 2.5 }} />

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.25} alignItems="flex-start">
            <CardGiftcardOutlined
              sx={{ color: "text.secondary", mt: 0.15, fontSize: 20 }}
            />
            <Box>
              <Typography variant="body2" fontWeight={700}>
                {lang?.transferGiftTitle || "Buying for someone else?"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {lang?.transferGiftBody ||
                  "Pay with a card and gift a credit package — they don’t need to be logged in with you."}
              </Typography>
            </Box>
          </Stack>
          <Button
            component={RouterLink}
            to="/gift-credits"
            variant="outlined"
            startIcon={<CardGiftcardOutlined />}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              whiteSpace: "nowrap",
              color: "text.primary",
              borderColor: theme.palette.divider,
              "&:hover": {
                borderColor: "primary.main",
                bgcolor: alpha(theme.palette.primary.main, 0.06),
              },
            }}
          >
            {lang?.transferGiftCta || "Gift credits"}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
